import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Spinner } from '@components/common/Spinner';
import { ppeAnalysisService } from '@services/ppe-analysis.service';
import { pdfAnalysisService } from '@services/pdf-analysis.service';
import { track } from '@services/analytics';
import { IndicatorFamily } from './IndicatorFamily';
import {
  PpeDocument,
  PpeDocumentStatus,
  StandardizedAnalysis,
  AnalysisStatus,
  AnalysisRun,
} from '../../types/index';

interface PpeAnalysisTabProps {
  propertyId: string;
}

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const STATUS_CONFIG: Record<PpeDocumentStatus, { label: string; color: string }> = {
  UPLOADED: { label: 'Prêt', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  EXTRACTING: { label: 'Extraction...', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  DONE: { label: 'Traité', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  ERROR: { label: 'Erreur', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
};

const FAMILY_LABELS: Record<string, string> = {
  financial: 'Analyse financière',
  technical: 'Analyse technique',
  energy: 'Analyse énergétique',
  governance: 'Gouvernance',
  risk: 'Analyse des risques',
};

const ALL_FAMILIES = ['financial', 'technical', 'energy', 'governance', 'risk'];

const getScoreColor = (score: number): string => {
  if (score >= 8) return 'from-green-500 to-green-600';
  if (score >= 6) return 'from-blue-500 to-blue-600';
  if (score >= 4) return 'from-yellow-500 to-yellow-600';
  if (score >= 2) return 'from-orange-500 to-orange-600';
  return 'from-red-500 to-red-600';
};

const getScoreLabel = (score: number): string => {
  if (score >= 8) return 'Excellent';
  if (score >= 6) return 'Bon';
  if (score >= 4) return 'Moyen';
  if (score >= 2) return 'Faible';
  return 'Critique';
};

const getScoreBadgeColor = (score: number): string => {
  if (score >= 8) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  if (score >= 6) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  if (score >= 4) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
};

export const PpeAnalysisTab: React.FC<PpeAnalysisTabProps> = ({ propertyId }) => {
  const [documents, setDocuments] = useState<PpeDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>({
    status: 'idle',
    phase: '',
    currentDoc: '',
    docsTotal: 0,
    docsDone: 0,
    currentFamily: '',
    familiesDone: [],
    progress: 0,
  });
  const [analysis, setAnalysis] = useState<StandardizedAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [history, setHistory] = useState<AnalysisRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const analysisPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load documents, existing indicators, history, and restore running analysis on mount
  useEffect(() => {
    const load = async () => {
      // Load documents (retry once if backend is busy)
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const docs = await ppeAnalysisService.listDocuments(propertyId);
          setDocuments(docs);
          break;
        } catch (err) {
          if (attempt === 1) console.error('Erreur chargement documents:', err);
          else await new Promise((r) => setTimeout(r, 2000));
        }
      }
      setIsLoadingDocs(false);

      // Check if an analysis is currently running → resume polling
      try {
        const currentStatus = await pdfAnalysisService.getAnalysisStatus(propertyId);
        if (currentStatus.status === 'running') {
          // Restore running state — the polling useEffect will kick in automatically
          setAnalysisStatus(currentStatus);
          return; // Polling will handle indicators/history once done
        }
      } catch {
        // No running analysis — continue with normal load
      }

      try {
        const existing = await pdfAnalysisService.getIndicators(propertyId);
        if (existing) {
          setAnalysis(existing);
          setAnalysisStatus({ status: 'done', phase: '', currentDoc: '', docsTotal: 0, docsDone: 0, currentFamily: '', familiesDone: [...ALL_FAMILIES], progress: 10 });
        }
      } catch {
        // No existing analysis — ok
      }

      try {
        const runs = await pdfAnalysisService.getHistory(propertyId);
        setHistory(runs);
      } catch {
        // No history — ok
      }
    };
    load();
  }, [propertyId]);

  // Poll analysis status + refresh documents while running
  useEffect(() => {
    if (analysisStatus.status !== 'running') return;

    analysisPollingRef.current = setInterval(async () => {
      try {
        const [status, docs] = await Promise.all([
          pdfAnalysisService.getAnalysisStatus(propertyId),
          ppeAnalysisService.listDocuments(propertyId),
        ]);
        setDocuments(docs);
        setAnalysisStatus(status);

        if (status.status === 'done') {
          clearInterval(analysisPollingRef.current!);
          const [result, runs] = await Promise.all([
            pdfAnalysisService.getIndicators(propertyId),
            pdfAnalysisService.getHistory(propertyId),
          ]);
          if (result) {
            setAnalysis(result);
            setSelectedRunId(null);
          }
          setHistory(runs);
        } else if (status.status === 'error') {
          clearInterval(analysisPollingRef.current!);
          setAnalysisError(status.error || "Erreur lors de l'analyse");
          const runs = await pdfAnalysisService.getHistory(propertyId);
          setHistory(runs);
        }
      } catch {
        // Ignore polling errors
      }
    }, 3000);

    return () => {
      if (analysisPollingRef.current) clearInterval(analysisPollingRef.current);
    };
  }, [analysisStatus.status, propertyId]);

  // Upload handler (instant: just MinIO + PostgreSQL)
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setUploading(true);
      for (const file of acceptedFiles) {
        try {
          const doc = await ppeAnalysisService.uploadDocument(propertyId, file);
          setDocuments((prev) => [doc, ...prev]);
        } catch (error) {
          console.error('Erreur upload:', error);
          alert(`Erreur lors de l'upload de ${file.name}`);
        }
      }
      setUploading(false);
    },
    [propertyId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    multiple: true,
    disabled: uploading,
  });

  const handleDelete = async (docId: string) => {
    try {
      await ppeAnalysisService.deleteDocument(docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      setDeletingId(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
    }
  };

  const handleRunAnalysis = async () => {
    setAnalysisError(null);
    setAnalysisStatus({
      status: 'running',
      phase: 'extracting',
      currentDoc: '',
      docsTotal: documents.length,
      docsDone: 0,
      currentFamily: '',
      familiesDone: [],
      progress: 0,
    });
    try {
      await pdfAnalysisService.analyzeWithRAG(propertyId);
      track('ppe-analysis-started', { docs: documents.length });
      // Polling takes over from here
    } catch (err: any) {
      setAnalysisStatus({ status: 'error', phase: '', currentDoc: '', docsTotal: 0, docsDone: 0, currentFamily: '', familiesDone: [], progress: 0, error: err.message });
      setAnalysisError(err.message || "Erreur lors du lancement de l'analyse");
    }
  };

  const handleSelectRun = (run: AnalysisRun) => {
    if (selectedRunId === run.id) {
      setSelectedRunId(null);
    } else {
      setSelectedRunId(run.id);
      if (run.analysisData) {
        setAnalysis(run.analysisData);
      }
    }
  };

  if (isLoadingDocs) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  const isRunning = analysisStatus.status === 'running';
  const canAnalyze = documents.length > 0 && !isRunning;

  // Progress label based on phase
  const getProgressLabel = () => {
    if (!isRunning) return '';
    const { phase, currentDoc, docsDone, docsTotal, currentFamily } = analysisStatus;
    if (phase === 'extracting') {
      return currentDoc ? `Extraction : ${currentDoc} (${(docsDone || 0) + 1}/${docsTotal || '?'})` : 'Extraction des documents...';
    }
    if (phase === 'embedding') return 'Construction de la base de connaissances...';
    if (phase === 'analyzing') return currentFamily ? `${FAMILY_LABELS[currentFamily] || currentFamily}...` : 'Analyse...';
    return 'Initialisation...';
  };

  return (
    <div className="space-y-6">

      {/* ─── Zone Upload ─── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
          Documents PPE
        </h3>

        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors duration-200 ${
            isDragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
          }`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <Spinner size="sm" />
              <span className="text-gray-600 dark:text-gray-400 text-sm">Upload en cours...</span>
            </div>
          ) : (
            <>
              <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {isDragActive ? 'Déposez les fichiers ici...' : 'Glissez-déposez ou cliquez pour sélectionner'}
              </p>
              <p className="mt-1 text-xs text-gray-500">PDF, Excel, Images (JPEG, PNG, WebP)</p>
            </>
          )}
        </div>
      </div>

      {/* ─── Liste des documents ─── */}
      {documents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Documents ({documents.length})
          </h3>
          <div className="space-y-2">
            {documents.map((doc) => {
              const statusCfg = STATUS_CONFIG[doc.status];
              return (
                <div
                  key={doc.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900"
                >
                  {/* Icône */}
                  <div className="flex-shrink-0">
                    {doc.mimeType === 'application/pdf' ? (
                      <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
                      </svg>
                    ) : doc.mimeType.startsWith('image/') ? (
                      <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM6 20V4h7v5h5v11H6z" />
                      </svg>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                      {doc.originalName}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      <span>{formatSize(doc.size)}</span>
                      {doc.status === 'ERROR' && doc.errorMessage && (
                        <>
                          <span>·</span>
                          <span className="text-red-500 dark:text-red-400 truncate max-w-xs" title={doc.errorMessage}>
                            {doc.errorMessage}
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Badge statut */}
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusCfg.color}`}>
                    {statusCfg.label}
                  </span>

                  {/* Ouvrir */}
                  <button
                    onClick={async () => {
                      try {
                        const objectUrl = await ppeAnalysisService.openDocument(doc.id);
                        const a = document.createElement('a');
                        a.href = objectUrl;
                        a.target = '_blank';
                        a.rel = 'noopener noreferrer';
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
                      } catch (err) {
                        console.error('Erreur ouverture document:', err);
                      }
                    }}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex-shrink-0"
                    title="Ouvrir le document"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Ouvrir
                  </button>

                  {/* Supprimer */}
                  {deletingId === doc.id ? (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="px-2 py-1 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded transition-colors"
                      >
                        Oui
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        Non
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(doc.id)}
                      disabled={isRunning}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors disabled:opacity-30 flex-shrink-0"
                      title="Supprimer"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Supprimer
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Section Analyse RAG ─── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200">
            Analyse RAG
          </h3>
          <button
            onClick={handleRunAnalysis}
            disabled={!canAnalyze}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {isRunning ? (
              <>
                <Spinner size="sm" />
                Analyse en cours...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {analysis ? 'Relancer l\'analyse' : 'Lancer l\'analyse RAG'}
              </>
            )}
          </button>
        </div>

        {documents.length === 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Uploadez au moins un document PPE pour lancer l'analyse.
          </p>
        )}

        {/* Progression étendue */}
        {isRunning && (
          <div className="mt-2 space-y-3">
            {/* Phase actuelle */}
            <div>
              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                <span className="animate-pulse">{getProgressLabel()}</span>
                <span>{analysisStatus.progress}/10</span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${((analysisStatus.progress || 0) / 10) * 100}%` }}
                />
              </div>
            </div>

            {/* Chips familles (uniquement en phase analyzing) */}
            {analysisStatus.phase === 'analyzing' && (
              <div className="flex flex-wrap gap-2">
                {ALL_FAMILIES.map((family) => {
                  const isDone = analysisStatus.familiesDone.includes(family);
                  const isCurrent = analysisStatus.currentFamily === family;
                  return (
                    <span
                      key={family}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        isDone
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                          : isCurrent
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 animate-pulse'
                            : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                      }`}
                    >
                      {isDone && <span className="mr-1">✓</span>}
                      {FAMILY_LABELS[family] || family}
                    </span>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-400 dark:text-gray-500">
              L'analyse peut prendre 5-35 minutes selon les documents. Vous pouvez naviguer et revenir.
            </p>
          </div>
        )}

        {/* Erreur analyse */}
        {analysisStatus.status === 'error' && analysisError && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{analysisError}</p>
          </div>
        )}
      </div>

      {/* ─── Résultats RAG ─── */}
      {analysis && (
        <div className="space-y-4">
          <div className={`bg-gradient-to-r ${getScoreColor(analysis.globalScore)} rounded-xl p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Score global PPE</p>
                <p className="text-4xl font-bold mt-1">{analysis.globalScore}/10</p>
                <p className="text-sm mt-1 opacity-80">{getScoreLabel(analysis.globalScore)}</p>
              </div>
              <div className="text-right text-xs opacity-70">
                <p>Modèle: {analysis.llmModel}</p>
                <p>Analysé le: {new Date(analysis.analyzedAt).toLocaleDateString('fr-CH')}</p>
                <p>{analysis.families.length} familles</p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {analysis.families.map((family) => (
              <IndicatorFamily key={family.family} family={family} />
            ))}
          </div>

          {(analysis.risks.length > 0 || analysis.opportunities.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analysis.risks.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">
                    Risques identifiés
                  </h3>
                  <ul className="space-y-2">
                    {analysis.risks.map((risk, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          risk.severity === 'critique' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                          risk.severity === 'eleve' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' :
                          risk.severity === 'moyen' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {risk.severity}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">{risk.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {analysis.opportunities.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-sm font-semibold text-green-600 dark:text-green-400 mb-3">
                    Opportunités identifiées
                  </h3>
                  <ul className="space-y-2">
                    {analysis.opportunities.map((opp, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          opp.potential === 'eleve' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                          opp.potential === 'moyen' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {opp.potential}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400">{opp.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Historique des analyses ─── */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
            Historique des analyses ({history.length})
          </h3>
          <div className="space-y-2">
            {history.map((run) => (
              <div
                key={run.id}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                  selectedRunId === run.id
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-100 dark:border-gray-700'
                }`}
              >
                {/* Clickable area */}
                <button
                  onClick={() => handleSelectRun(run)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                >
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {run.status === 'DONE' ? (
                      <span className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center text-green-600 dark:text-green-400 text-xs">✓</span>
                    ) : run.status === 'ERROR' ? (
                      <span className="w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-red-600 dark:text-red-400 text-xs">✗</span>
                    ) : (
                      <span className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                        <Spinner size="sm" />
                      </span>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {new Date(run.startedAt).toLocaleString('fr-CH', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {run.docsUsed} doc{run.docsUsed > 1 ? 's' : ''} analysé{run.docsUsed > 1 ? 's' : ''}
                      {run.docsErrored > 0 && ` · ${run.docsErrored} en erreur`}
                      {run.llmModel && ` · ${run.llmModel}`}
                    </p>
                    {run.status === 'ERROR' && run.errorMessage && (
                      <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 truncate">{run.errorMessage}</p>
                    )}
                  </div>

                  {/* Score */}
                  {run.globalScore != null && (
                    <span className={`px-2 py-1 rounded-lg text-sm font-bold flex-shrink-0 ${getScoreBadgeColor(run.globalScore)}`}>
                      {run.globalScore}/10
                    </span>
                  )}
                </button>

                {/* Delete button (not shown for RUNNING) */}
                {run.status !== 'RUNNING' && (
                  <button
                    onClick={async () => {
                      try {
                        await pdfAnalysisService.deleteRun(run.id);
                        setHistory((prev) => prev.filter((r) => r.id !== run.id));
                        if (selectedRunId === run.id) setSelectedRunId(null);
                      } catch (err) {
                        console.error('Delete run error:', err);
                      }
                    }}
                    title="Supprimer"
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))}
          </div>
          {selectedRunId && (
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 text-center">
              Cliquez à nouveau sur l'entrée pour revenir au résultat actuel
            </p>
          )}
        </div>
      )}

      {/* État vide */}
      {documents.length === 0 && (
        <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
          <p>Aucun document PPE pour cette propriété</p>
          <p className="mt-1 text-xs">
            Uploadez des comptes annuels, PV d'assemblée ou budgets pour lancer une analyse RAG
          </p>
        </div>
      )}
    </div>
  );
};
